import {
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  ContentLayout,
  Flashbar,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import {
  InspectionValues,
  PartTypes,
  StepTypes,
} from '@parallel-fluidics/shared/constants';
import { travelerFromFirestore, travelerLastStepPassed } from '@parallel-fluidics/shared/models/travelers';
import { useQueryClient } from '@tanstack/react-query';
import saveTravelerWithInstalls from 'features/lots/api/saveTravelerWithInstalls';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { Formik } from 'formik';
import {
  useGetOrderById,
  useGetProjectById,
  useGetProjectParts,
} from 'hooks/use-basicQueries';
import { useUserData } from 'providers/userDataProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as Yup from 'yup';

import Changelog from './changelog';
import TravelerStepCard from './components/TravelerStepCard';
import TransferTravelerModal from './transferModal';
import UnsavedAlert from './unsavedAlert';

const validationSchema = Yup.object().shape({
  steps: Yup.array().of(
    Yup.object().shape({
      inspected: Yup.string().test(
        'steps-sequence',
        'All previous steps must be marked as Passed for this step can be marked as Passed. Consider marking this step as "Tuning."',
        (value, context) => {
          if (value === InspectionValues.PASSED) {
            const { options } = context;
            const stepIndex = options.index; // Get the index of the current step
            const { steps } = options.context;
            const previousSteps = steps.slice(0, stepIndex);
            return previousSteps.every(
              (step) => step.inspected === InspectionValues.PASSED
              || step.type === StepTypes.INSPECTION,
            );
          }
          return true;
        },
      ),
      minThickness: Yup.number()
        .nullable()
        .when('type', {
          is: StepTypes.MOLDING,
          then: (schema) => schema.positive('minThickness must be greater than 0'),
        }),
      maxThickness: Yup.number()
        .nullable()
        .when(['type', 'minThickness'], {
          is: (type, minThickness) => type === StepTypes.MOLDING && minThickness !== null,
          then: (schema) => schema
            .moreThan(Yup.ref('minThickness'), 'maxThickness must be greater than minThickness'),
        }),
      inspectionReport: Yup.array().of(
        Yup.object().shape({
          downloadURL: Yup.string().url('Must be a valid URL').required('Download URL is required'),
          lastModifiedTS: Yup.number().required('Last Modified Timestamp is required'),
          name: Yup.string().required('Name is required'),
          ref: Yup.string().required('Reference URL is required'),
          type: Yup.string().required('Type is required'),
        }),
      ),
    }),
  ),
});

function TravelerPage() {
  const { user } = useUserData();
  const { travelerId } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [traveler, setTraveler] = useState(null);
  const [travelerStatus, setTravelerStatus] = useState('loading');
  const [travelerError, setTravelerError] = useState(null);

  const [openTransferModal, setOpenTransferModal] = useState(false);

  useEffect(() => {
    if (!travelerId) return undefined;
    const unsubscribe = onSnapshot(
      doc(getFirestore(), 'travelers', travelerId),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setTraveler(travelerFromFirestore(docSnapshot));
          setTravelerStatus('success');
        } else {
          setTraveler(null);
          setTravelerStatus('error');
          setTravelerError('Traveler not found');
        }
      },
      (err) => {
        setTravelerStatus('error');
        setTravelerError(err.message);
      },
    );

    return () => unsubscribe();
  }, [travelerId]);

  const { data: order = {}, isFetching, isError } = useGetOrderById(traveler?.order);
  const { data: project, isLoading: projectLoading } = useGetProjectById(traveler?.project);

  const { data: pParts } = useGetProjectParts({
    projectDocId: project?.id,
    type: PartTypes.PRODUCT,
  });

  const orderSteps = useMemo(() => {
    if (isFetching || isError || !traveler?.lineItem) {
      return [];
    }
    const foundLineItem = order.shipments?.flatMap((shipment) => shipment.lineItems)
      .find((lineItem) => lineItem.id === traveler?.lineItem);
    return foundLineItem?.steps || [];
  }, [isError, isFetching, order.shipments, traveler?.lineItem]);

  const initialValues = useMemo(() => {
    if (!traveler || travelerStatus !== 'success') {
      return {};
    }
    // Default minThickness and maxThickness to prevent false dirty state
    const steps = traveler.steps?.map((step) => {
      if (step.type === StepTypes.MOLDING) {
        return {
          ...step,
          minThickness: step.minThickness || '',
          maxThickness: step.maxThickness || '',
        };
      }
      return step;
    }) || [];
    return { ...traveler, steps, pendingInstalls: {} };
  }, [traveler, travelerStatus]);

  const queryClient = useQueryClient();

  // Per-step BOM coverage, reported up by each StepComponentsField (the
  // component owns the part fetch); missing/null entries mean no BOM.
  const [coverageByStep, setCoverageByStep] = useState({});

  // Which step rows are open. The current step — the first one that isn't
  // Passed — starts open, and so does any step inspected as rejected or
  // tuning, since those need attention. Passed steps are done and
  // not-yet-inspected later steps aren't up yet, so both start collapsed
  // to keep the focus on one step. Recomputed whenever the saved traveler
  // changes, so the focus moves once a Passed is saved; manual toggles
  // hold until then.
  const [expandedSteps, setExpandedSteps] = useState({});
  useEffect(() => {
    const steps = initialValues.steps || [];
    const currentIndex = steps.findIndex((step) => step.inspected !== InspectionValues.PASSED);
    const needsAttention = (step) => Boolean(step.inspected)
      && step.inspected !== InspectionValues.PASSED
      && step.inspected !== InspectionValues.NOT_INSPECTED;
    setExpandedSteps(Object.fromEntries(
      steps.map((step, index) => [index, index === currentIndex || needsAttention(step)]),
    ));
  }, [initialValues]);
  const anyExpanded = Object.values(expandedSteps).some(Boolean);
  const toggleAll = () => setExpandedSteps(Object.fromEntries(
    (initialValues.steps || []).map((step, index) => [index, !anyExpanded]),
  ));
  const handleCoverageChange = useCallback((stepIndex, coverage) => {
    setCoverageByStep((prev) => ({ ...prev, [stepIndex]: coverage }));
  }, []);

  // Hard rule: a step whose part has a BOM can't be Passed until covered.
  const validateInstalls = useCallback((formValues) => {
    const stepErrors = [];
    let hasError = false;
    (formValues.steps || []).forEach((step, index) => {
      const coverage = coverageByStep[index];
      if (!coverage) return;
      if (step.inspected !== InspectionValues.PASSED) return;
      if (!coverage.covered) {
        stepErrors[index] = {
          inspected: 'All BOM components must be scanned before this step can be marked as Passed.',
        };
        hasError = true;
      }
    });
    return hasError ? { steps: stepErrors } : {};
  }, [coverageByStep]);

  if (travelerStatus === 'loading') {
    return (
      <ContentLayout
        header={(
          <Header variant="h2">
            Edit Traveler
          </Header>
        )}
      >
        <Container>
          {' '}
          <Spinner />
        </Container>
      </ContentLayout>
    );
  }

  if (travelerStatus === 'error') {
    return (
      <ContentLayout
        header={(
          <Header variant="h2">
            Edit Traveler
          </Header>
        )}
      >
        <Container>
          <Flashbar
            items={[{
              header: 'Error loading traveler',
              content: travelerError,
              type: 'error',
            }]}
          />
        </Container>
      </ContentLayout>
    );
  }

  if (!traveler) {
    return (
      <ContentLayout
        header={(
          <Header variant="h2">
            {`Edit Traveler ${travelerId}`}
          </Header>
        )}
      >
        <Container>
          <Flashbar
            items={[{
              header: 'Traveler not found',
              content: `No traveler found with ID ${travelerId}. If you have a traveler sticker with this id, it might mean the traveler hasn't been used yet. Try starting a forming run with this travler!`,
              type: 'warning',
            }]}
          />
        </Container>
      </ContentLayout>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validate={validateInstalls}
        enableReinitialize
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          setError(null);

          try {
            // Inventory is handled server-side by the updatedTravelerV2 cloud
            // function, which reacts to step changes and sets productDocId /
            // productVersionId automatically. Newly scanned component bags
            // are consumed in the same transaction as the traveler update.
            const installs = Object.entries(values.pendingInstalls || {})
              .filter(([, lots]) => lots?.length > 0)
              .map(([stepIndex, lots]) => ({
                stepIndex: Number(stepIndex),
                newLotIds: lots.map((lot) => lot.id),
                residualLines: coverageByStep[Number(stepIndex)]?.residualLines || [],
              }));
            const {
              steps: savedSteps,
              componentLotIds: savedComponentLotIds,
            } = await saveTravelerWithInstalls({
              travelerId,
              steps: values.steps,
              shipped: values.shipped || false,
              installs,
              user: { uid: user.id, email: user.email },
            });
            // Reset to the saved state immediately — waiting for the
            // snapshot leaves a window where a second Save would consume
            // the same bags again (or re-submit stale componentLotIds).
            resetForm({
              values: {
                ...values,
                steps: savedSteps,
                componentLotIds: savedComponentLotIds,
                pendingInstalls: {},
              },
            });
            if (installs.length > 0) {
              // The consumed bags render in many cached places.
              queryClient.invalidateQueries({ queryKey: ['lot'] });
              queryClient.invalidateQueries({ queryKey: ['lotsByIds'] });
              queryClient.invalidateQueries({ queryKey: ['getLotsData'] });
              queryClient.invalidateQueries({ queryKey: ['travelersUsingLot'] });
            }
            toast.success('Traveler updated successfully');
          } catch (e) {
            setError(e.message);
            // reset form to initial values if there is an error
            resetForm();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          handleSubmit, setFieldValue, setValues, values, isSubmitting, dirty, errors,
        }) => (
          <ContentLayout
            header={(
              <Header
                variant="h2"
                actions={(
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!dirty}
                    loading={isSubmitting}
                  >
                    Save
                  </Button>
                )}
              >
                Edit Traveler
              </Header>
            )}
          >
            <UnsavedAlert dirty={dirty} />
            <SpaceBetween direction="vertical" size="l">
              {error ? (
                <Flashbar
                  items={[{
                    header: 'Error saving traveler',
                    content: error,
                    type: 'error',
                  }]}
                />
              ) : null}

              {/* Project */}
              <Container>
                <Header
                  variant="h3"
                  actions={(
                    <>
                      <Button
                        variant="link"
                        disabled={!travelerLastStepPassed(traveler.steps)}
                        disabledReason="Available once the last step is Passed."
                        onClick={() => navigate(`/print/product-label?travelerId=${travelerId}`)}
                      >
                        Print product label
                      </Button>
                      {user?.canMoveTraveler && (
                        <Button
                          variant="link"
                          onClick={() => setOpenTransferModal(true)}
                        >
                          Transfer
                        </Button>
                      )}
                    </>
                  )}
                >
                  {`Traveler ID: ${travelerId}`}
                </Header>
                <div className="even-columns">
                  <FormField label="Project">
                    {`${traveler.project}${traveler.pPart ? `-${traveler.pPart}` : ''}`}
                  </FormField>
                  <FormField label="Tool">
                    {traveler.tool}
                  </FormField>
                </div>
              </Container>

              {/* Steps */}
              <Container
                header={(
                  <Header
                    variant="h3"
                    counter={`(${values.steps?.filter((step) => step.inspected === InspectionValues.PASSED).length || 0} of ${values.steps?.length || 0} passed)`}
                    actions={(
                      <Button variant="inline-link" onClick={toggleAll}>
                        {anyExpanded ? 'Collapse all' : 'Expand all'}
                      </Button>
                    )}
                  >
                    Steps
                  </Header>
                )}
              >
                <ColumnLayout columns={1} borders="horizontal">
                  {values.steps?.map((step, index) => (
                    <TravelerStepCard
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      // A row with validation errors stays open: the
                      // sequence rule can put errors on collapsed Passed
                      // steps, and Formik blocks the save silently otherwise.
                      expanded={(expandedSteps[index] ?? true) || Boolean(errors.steps?.[index])}
                      onExpandedChange={(open) => setExpandedSteps(
                        (prev) => ({ ...prev, [index]: open }),
                      )}
                      step={step}
                      index={index}
                      previousSteps={values.steps.slice(0, index)}
                      errors={errors.steps?.[index]}
                      pendingLots={values.pendingInstalls?.[index] || []}
                      coverage={coverageByStep[index]}
                      pParts={pParts}
                      project={project}
                      projectLoading={projectLoading}
                      orderStepNote={orderSteps[index]?.note}
                      orderId={traveler?.order}
                      travelerId={travelerId}
                      setFieldValue={setFieldValue}
                      // Functional update against the latest form state —
                      // rapid scans (or two steps updating) must not clobber
                      // each other with a stale pendingInstalls snapshot.
                      onPendingChange={(update) => setValues((prevValues) => ({
                        ...prevValues,
                        pendingInstalls: {
                          ...prevValues.pendingInstalls,
                          [index]: update(prevValues.pendingInstalls?.[index] || []),
                        },
                      }))}
                      onCoverageChange={handleCoverageChange}
                    />
                  ))}
                </ColumnLayout>
              </Container>

              {/* Shipped */}
              <Container>
                <Header variant="h3">
                  Shipped
                </Header>
                <FormField
                  errorText={errors.shipped}
                  stretch
                >
                  <Checkbox
                    onChange={({ detail }) => {
                      setFieldValue('shipped', detail.checked);
                    }}
                    checked={values.shipped || false}
                  >
                    Shipped
                  </Checkbox>
                </FormField>
              </Container>
              {/* Save Button */}
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!dirty}
                loading={isSubmitting}
              >
                Save
              </Button>
            </SpaceBetween>
          </ContentLayout>
        )}
      </Formik>
      <Changelog travelerId={travelerId} />

      {openTransferModal && (
        <TransferTravelerModal
          isOpen={openTransferModal}
          onClose={() => setOpenTransferModal(false)}
          traveler={traveler}
        />
      )}
    </SpaceBetween>
  );
}

export default TravelerPage;
